import * as React from 'react'
import { useInvokeApi, useAuth } from '../../hooks'
import { Formik } from 'formik'
import { FormikSelect, EditModalActionBar } from '../../components'
import { Form, Box, Loader, Typography } from 'cs-kevlar-react'
import { Option } from 'cs-kevlar-react/lib/es/Select'
import { Grid } from 'cs-kevlar-react'
import {
  ImportConfigurationViewModel,
  ApiResult,
  EmptyApiResult,
  SupportUserPermission,
} from '../../api'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { getAllEnumOptions, hasOneOfPermissions } from '../../utils'
import { AxiosResponse } from 'axios'
import { FormikTextField } from '../FormikTextField'
import * as Yup from 'yup'

interface EditImportConfigurationProps {
  onClose?: () => void
  customerAgencyId: string | null
  getter: (
    customerAgencyId: string | null,
  ) => Promise<AxiosResponse<ApiResult<ImportConfigurationViewModel | null>>>
  updater: (
    newConfig: ImportConfigurationViewModel,
  ) => Promise<AxiosResponse<EmptyApiResult>>
  unsetFn?: () => Promise<AxiosResponse<EmptyApiResult>>
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    overrideMessage: {
      marginBottom: theme.spacing(8),
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'hidden',
      overflowX: 'hidden',
      padding: theme.spacing(8),
    },
  }),
)

interface Lazy<T> {
  hasValue: boolean
  value: T | null
}

const booleanOptions: Option[] = [
  {
    key: 'true',
    value: true,
    label: 'true',
  },
  {
    key: 'false',
    value: false,
    label: 'false',
  },
]

const lockAcquisitionDelayFieldProps = {
  type: 'number',
  min: '0',
  max: '30',
}

export const ImportConfigurationSchema = Yup.object().shape({
  enableLegacyDocumentImportDeduping: Yup.boolean().required(
    'You must choose one of the values.',
  ),
  enableDocumentImportDeduping: Yup.boolean().required(
    'You must choose one of the values.',
  ),
  enableLambdaThumbnailGeneration: Yup.boolean().required(
    'You must choose one of the values.',
  ),
  enableS3FileDocumentCleanup: Yup.boolean().required(
    'You must choose one of the values.',
  ),
  enableStatusingDocumentDebugLogs: Yup.boolean().required(
    'You must choose one of the values.',
  ),
  enableDeviceAvlPushMonitoring: Yup.boolean().required(
    'You must choose one of the values.',
  ),
  importLockAcquisitionDelayInSeconds: Yup.number()
    .min(0, 'The delay must not be negative.')
    .max(30, 'The delay must not be greater than 30 seconds.'),
})

export const EditImportConfiguration: React.FunctionComponent<EditImportConfigurationProps> = ({
  onClose,
  customerAgencyId,
  updater,
  getter,
  unsetFn,
}) => {
  const classes = useStyles()
  const invokeApi = useInvokeApi()
  const { user: authUser } = useAuth()
  const [importConfiguration, setImportConfiguration] = React.useState<
    Lazy<ImportConfigurationViewModel>
  >({ hasValue: false, value: null })

  React.useEffect(() => {
    invokeApi(
      () => getter(customerAgencyId),
      'There was an error getting the import configuration',
      (result) => {
        const value: ImportConfigurationViewModel =
          result !== null
            ? result
            : {
                enableLegacyDocumentImportDeduping: true,
                enableDocumentImportDeduping: true,
                enableLambdaThumbnailGeneration: true,
                enableS3FileDocumentCleanup: true,
                enableStatusingDocumentDebugLogs: false,
                importLockAcquisitionDelayInSeconds: 0,
                enableDeviceAvlPushMonitoring: true,
              }
        value.importLockAcquisitionDelayInSeconds =
          value.importLockAcquisitionDelayInSeconds ?? 0
        setImportConfiguration({ hasValue: true, value })
      },
    )
  }, [])

  const onEdit = (values: ImportConfigurationViewModel, actions: any) => {
    const newConfig: ImportConfigurationViewModel = {
      enableLegacyDocumentImportDeduping:
        values.enableLegacyDocumentImportDeduping,
      enableDocumentImportDeduping: values.enableDocumentImportDeduping,
      enableLambdaThumbnailGeneration: values.enableLambdaThumbnailGeneration,
      enableS3FileDocumentCleanup: values.enableS3FileDocumentCleanup,
      enableStatusingDocumentDebugLogs: values.enableStatusingDocumentDebugLogs,
      importLockAcquisitionDelayInSeconds:
        values.importLockAcquisitionDelayInSeconds,
      enableDeviceAvlPushMonitoring: values.enableDeviceAvlPushMonitoring,
    }
    invokeApi(
      () => updater(newConfig),
      'There was an error setting the import configuration',
      (_) => {},
      () => {
        if (onClose !== undefined) {
          onClose()
        }
      },
    )
  }

  if (authUser === null) return null

  const canEdit = hasOneOfPermissions(authUser, [
    SupportUserPermission.ManageInterfaceConfiguration,
  ])

  const removeOverrides = async () => {
    if (!unsetFn) {
      onClose && onClose()
      return
    }

    await unsetFn()
    onClose && onClose()
    return
  }

  return (
    <React.Fragment>
      {!importConfiguration.hasValue && (
        <Loader size="sm" text="Loading Import Configuration" />
      )}
      {importConfiguration.hasValue && (
        <Formik
          initialValues={
            importConfiguration.value as ImportConfigurationViewModel
          }
          validationSchema={ImportConfigurationSchema}
          onSubmit={onEdit}
        >
          {(formikProps) => (
            <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
              <Box className={classes.formFieldContainer}>
                {unsetFn && (
                  <Typography className={classes.overrideMessage}>
                    Any values specified here will override the globally
                    configured import configuration. Clicking the 'Remove
                    Overrides' will remove any customer interface specified
                    overrides and revert to the globally configured import
                    configurations.
                  </Typography>
                )}
                <Grid container direction="column">
                  <Grid item>
                    <FormikSelect
                      formikProps={formikProps}
                      id="EnableLegacyDocumentImportDeduping"
                      fieldName="enableLegacyDocumentImportDeduping"
                      label="Enable Legacy Document Import Deduping"
                      options={booleanOptions}
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikSelect
                      formikProps={formikProps}
                      id="EnableDocumentImportDeduping"
                      fieldName="enableDocumentImportDeduping"
                      label="Enable Document Import Deduping"
                      options={booleanOptions}
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikSelect
                      formikProps={formikProps}
                      id="EnableLambdaThumbnailGeneration"
                      fieldName="enableLambdaThumbnailGeneration"
                      label="Enable Lambda Thumbnail Generation"
                      options={booleanOptions}
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikSelect
                      formikProps={formikProps}
                      id="EnableS3FileDocumentCleanup"
                      fieldName="enableS3FileDocumentCleanup"
                      label="Enable S3 File Document Cleanup"
                      options={booleanOptions}
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName="importLockAcquisitionDelayInSeconds"
                      label="Import Lock Acquisition Delay In Seconds"
                      inputProps={lockAcquisitionDelayFieldProps}
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikSelect
                      formikProps={formikProps}
                      id="EnableStatusingDocumentDebugLogs"
                      fieldName="enableStatusingDocumentDebugLogs"
                      label="Enable Statusing Document Debug Logs"
                      options={booleanOptions}
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikSelect
                      formikProps={formikProps}
                      id="EnableDeviceAvlPushMonitoring"
                      fieldName="enableDeviceAvlPushMonitoring"
                      label="Enable Device Avl Push Monitoring"
                      options={booleanOptions}
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Box className={classes.spacer} />
                </Grid>
              </Box>
              {
                <EditModalActionBar
                  canDelete={!!unsetFn}
                  deleteText="Remove Overrides"
                  onDelete={removeOverrides}
                  onCancel={onClose}
                  canSubmit={canEdit}
                  formikProps={formikProps}
                />
              }
            </Form>
          )}
        </Formik>
      )}
    </React.Fragment>
  )
}
